import React from "react";
import {
  AppBar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  Divider,
  Link,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import GitHubIcon from "@mui/icons-material/GitHub";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the (currently commented-out) How-to Guides section
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

// ---------------------------------------------------------------------------
// Configuration — fill these in as URLs are finalized. Leave a link value empty
// ("") to show the tile as "coming soon" instead of a live link.
// ---------------------------------------------------------------------------
const LINKS = {
  // NTI's "A Proposal for BioDesign Metadata Exchange" white paper.
  whitepaper:
    "https://www.nti.org/analysis/articles/white-paper-a-proposal-for-biodesign-metadata-exchange-for-use-in-biosecurity/",
  github: "https://github.com/Lattice-Automation/Biodesign-Metadata-Exchange/tree/phase-2-updates",
  certUi: "https://dxhx8ws8s7x9z.cloudfront.net/tools",
  interpretationTool: "https://staging.d6q6ydaou6rbk.amplifyapp.com",
};

// Demo video served from public/. Set to "" to show a placeholder instead.
const DEMO_VIDEO_SRC = "/bmde-demo.mp4";

// How-to guide PDFs. Point each `href` at the published PDF (e.g. "/guides/integrate.pdf"
// placed in public/, or an external URL). Leave "" to show "Coming soon".
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by the (currently commented-out) How-to Guides section
const GUIDES = {
  integrate: "", // TODO: "How to integrate with your design tool" PDF
  interpret: "", // TODO: "How to evaluate / interpret the metadata" PDF
  getInvolved: "", // TODO: "How to learn more and get more involved" PDF
};

const CONTACT_EMAIL = "chris.krenz@latticeautomation.com";

const NTI_URL = "https://www.nti.org/";
const LATTICE_URL = "https://latticeautomation.com/";

// ---------------------------------------------------------------------------
// Header: NTI logo (left), Lattice logo (right)
// ---------------------------------------------------------------------------
const Header: React.FC = () => (
  <AppBar
    position="sticky"
    color="inherit"
    elevation={0}
    sx={{
      bgcolor: "background.paper",
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    }}
  >
    <Toolbar sx={{ minHeight: { xs: 60, md: 72 } }}>
      <Box component="a" href={NTI_URL} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex" }}>
        <Box component="img" src="/NTIbio.png" alt="NTI | bio" sx={{ height: 28, objectFit: "contain" }} />
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Box component="a" href={LATTICE_URL} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex" }}>
        <Box component="img" src="/Lattice.png" alt="Lattice Automation" sx={{ height: 34, objectFit: "contain" }} />
      </Box>
    </Toolbar>
  </AppBar>
);

// ---------------------------------------------------------------------------
// One of the four link tiles (2 x 2 grid)
// ---------------------------------------------------------------------------
const LinkTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  href: string;
  ctaLabel?: string;
}> = ({ icon, title, description, href, ctaLabel = "Open" }) => {
  const enabled = Boolean(href);
  return (
    <Card sx={{ height: "100%" }}>
      <CardActionArea
        component={enabled ? "a" : "div"}
        href={enabled ? href : undefined}
        target={enabled ? "_blank" : undefined}
        rel={enabled ? "noopener noreferrer" : undefined}
        disabled={!enabled}
        sx={{ height: "100%", p: { xs: 3, md: 4 } }}
      >
        <Stack spacing={1.5} alignItems="flex-start" sx={{ height: "100%" }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 1.5,
              borderRadius: 2,
              bgcolor: "primary.lighter",
              color: "primary.main",
            }}
          >
            {icon}
          </Box>
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {description}
          </Typography>
          {enabled ? (
            <Typography variant="subtitle2" color="primary.main">
              {ctaLabel} &rarr;
            </Typography>
          ) : (
            <Chip size="small" label="Coming soon" />
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const App: React.FC = () => {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Header />

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, flexGrow: 1 }}>
        {/* Hero / description */}
        <Stack spacing={2} sx={{ maxWidth: 820, mx: "auto", textAlign: "center", mb: { xs: 6, md: 8 } }}>
          <Typography variant="h2">Biodesign Metadata<br/>Security Standard (BMSS)</Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
            Standardized metadata tracking how a biological construct was designed.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            As AI-enabled tools make it easier to design novel sequences, synthesis providers and regulators need more sophisticated ways of evaluating biodesign safety, beyond homology-based screening.
            <br/><br/>BMSS defines a common, machine-readable, and exportable metadata format consisting of a cryptographically signed design provenance that can be authenticated against a public trust list.
          </Typography>
        </Stack>

        {/* 2 x 2 link grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 2, md: 3 },
            maxWidth: 900,
            mx: "auto",
          }}
        >
          <LinkTile
            icon={<MenuBookOutlinedIcon sx={{ fontSize: 32 }} />}
            title="White Paper"
            description="The original proposal introducing the standard and the problem it solves."
            href={LINKS.whitepaper}
          />
          <LinkTile
            icon={<GitHubIcon sx={{ fontSize: 32 }} />}
            title="GitHub Repository"
            description="Open-source libraries, reference implementation, and the metadata specification."
            href={LINKS.github}
          />
          <LinkTile
            icon={<VerifiedOutlinedIcon sx={{ fontSize: 32 }} />}
            title="Certification Portal"
            description="Where design-tool providers get certified and their signing keys are published to the trust list."
            href={LINKS.certUi}
          />
          <LinkTile
            icon={<BiotechOutlinedIcon sx={{ fontSize: 32 }} />}
            title="Interpretation Tool"
            description="View and interpret a design's metadata — its edit history and signature — in the browser."
            href={LINKS.interpretationTool}
          />
        </Box>

        {/* Demo video */}
        <Stack spacing={2} sx={{ maxWidth: 900, mx: "auto", mt: { xs: 7, md: 10 } }}>
          <Typography variant="h4" textAlign="center">
            See it in action
          </Typography>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              pt: "56.25%",
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "grey.900",
              boxShadow: 8,
            }}
          >
            {DEMO_VIDEO_SRC ? (
              <Box
                component="video"
                src={DEMO_VIDEO_SRC}
                controls
                preload="metadata"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                  objectFit: "contain",
                  bgcolor: "grey.900",
                }}
              />
            ) : (
              <Stack
                spacing={1}
                sx={{ position: "absolute", inset: 0, alignItems: "center", justifyContent: "center", color: "grey.100" }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 72 }} />
                <Typography variant="subtitle1">Demo video coming soon</Typography>
              </Stack>
            )}
          </Box>
        </Stack>

        {/* How-to guides */}
        {/* <Stack spacing={2} sx={{ maxWidth: 900, mx: "auto", mt: { xs: 7, md: 10 } }}>
          <Typography variant="h4" textAlign="center">
            How-to Guides
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: { xs: 2, md: 3 },
            }}
          >
            <LinkTile
              icon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 32 }} />}
              title="Capture metadata"
              description="Add BMDE metadata export and signing to your design tool."
              href={GUIDES.integrate}
              ctaLabel="View PDF"
            />
            <LinkTile
              icon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 32 }} />}
              title="Evaluate metadata"
              description="Read, verify, and interpret a design's metadata and signature."
              href={GUIDES.interpret}
              ctaLabel="View PDF"
            />
            <LinkTile
              icon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 32 }} />}
              title="Learn more"
              description="Understand the standard, its goals, and how to contribute."
              href={GUIDES.getInvolved}
              ctaLabel="View PDF"
            />
          </Box>
        </Stack> */}

        {/* Contact */}
        <Stack spacing={1.5} sx={{ maxWidth: 900, mx: "auto", mt: { xs: 7, md: 10 }, textAlign: "center" }}>
          <Typography variant="h4">Contact</Typography>
          <Typography variant="body1" color="text.secondary">
            Questions about the standard, certification, or getting your tool integrated?
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <EmailOutlinedIcon color="primary" />
            <Link href={`mailto:${CONTACT_EMAIL}`} variant="subtitle1" underline="hover">
              {CONTACT_EMAIL}
            </Link>
          </Stack>
        </Stack>
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 3 }}
          >
            <Typography variant="caption" color="text.secondary">
              &copy; {new Date().getFullYear()} Nuclear Threat Initiative &amp; Lattice Automation
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" divider={<Divider orientation="vertical" flexItem />}>
              <Link href={NTI_URL} target="_blank" rel="noopener noreferrer" variant="caption" color="text.secondary" underline="hover">
                NTI
              </Link>
              <Link href={LATTICE_URL} target="_blank" rel="noopener noreferrer" variant="caption" color="text.secondary" underline="hover">
                Lattice Automation
              </Link>
              <Link href={LINKS.github} target="_blank" rel="noopener noreferrer" variant="caption" color="text.secondary" underline="hover">
                GitHub
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default App;
